import React, { useState } from 'react';
import Button from './Button';
import Input from './Input';
import Textarea from './Textarea';
import SelectDropdown from './SelectDropdown';
import RadioButtonGroup from './RadioButtonGroup';
import MultiStepFormDemo from './MultiStepForm';
import './ComponentsDemo.scss';

const ComponentsDemo = () => {
  const [formState, setFormState] = useState({
    bannerType: 'button',
    title: '',
    description: '',
    primaryButtonText: '',
    primaryButtonLink: '',
    secondaryButtonText: '',
    secondaryButtonLink: '',
    buttonColor: 'white',
  });

  const handleChange = (field) => (value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formState);
    alert('Check console for form data');
  };

  const handleReset = () => {
    setFormState({
      bannerType: 'button',
      title: '',
      description: '',
      primaryButtonText: '',
      primaryButtonLink: '',
      secondaryButtonText: '',
      secondaryButtonLink: '',
      buttonColor: 'white',
    });
  };

  return (
    <div className="components-demo">
      <div className="demo-header">
        <h1>Base UI Components Demo</h1>
        <p>Reusable components based on ABO Academy Figma design</p>
      </div>

      <div className="demo-section">
        <h2>Buttons</h2>
        <div className="button-grid">
          <Button variant="primary">Primary Button</Button>
          <Button variant="secondary">Secondary Button</Button>
          <Button variant="outline">Outline Button</Button>
          <Button variant="ghost">Ghost Button</Button>
          <Button variant="primary" disabled>
            Disabled Button
          </Button>
          <Button variant="primary" size="small">
            Small Button
          </Button>
          <Button variant="primary" size="large">
            Large Button
          </Button>
        </div>
      </div>

      <div className="demo-section">
        <h2>Complete Form Example</h2>
        <form onSubmit={handleSubmit} className="demo-form">
          <RadioButtonGroup
            label="Banner Type"
            name="bannerType"
            options={[
              { value: 'button', label: 'Button Link' },
              { value: 'image', label: 'Image Link' },
            ]}
            value={formState.bannerType}
            onChange={handleChange('bannerType')}
            showInfoIcon
            required
          />

          <Input
            label="Title"
            placeholder="Enter Banner Title"
            value={formState.title}
            onChange={handleChange('title')}
            name="title"
            maxLength={45}
            charLimit={45}
            required
          />

          <Textarea
            label="Description"
            placeholder="Enter Banner Description"
            value={formState.description}
            onChange={handleChange('description')}
            name="description"
            maxLength={150}
            charLimit={150}
            rows={3}
            showDragHandle
          />

          <div className="form-row">
            <Input
              label="Primary Button Text"
              placeholder="e. g. Learn more"
              value={formState.primaryButtonText}
              onChange={handleChange('primaryButtonText')}
              name="primaryButtonText"
              maxLength={15}
              charLimit="Max 15 chars"
              required
            />

            <Input
              label="Primary Button Link"
              placeholder="https://"
              type="url"
              value={formState.primaryButtonLink}
              onChange={handleChange('primaryButtonLink')}
              name="primaryButtonLink"
              required
            />
          </div>

          <div className="form-row">
            <Input
              label="Secondary Button Text"
              placeholder="e. g. Learn more"
              value={formState.secondaryButtonText}
              onChange={handleChange('secondaryButtonText')}
              name="secondaryButtonText"
              maxLength={15}
              charLimit="Max 15 chars"
            />

            <Input
              label="Secondary Button Link"
              placeholder="https://"
              type="url"
              value={formState.secondaryButtonLink}
              onChange={handleChange('secondaryButtonLink')}
              name="secondaryButtonLink"
            />
          </div>

          <SelectDropdown
            label="Button and Text Color"
            options={[
              { value: 'white', label: 'White' },
              { value: 'black', label: 'Black' },
            ]}
            value={formState.buttonColor}
            onChange={handleChange('buttonColor')}
            name="buttonColor"
          />

          <div className="form-actions">
            <Button variant="secondary" type="button" onClick={handleReset}>
              Reset Form
            </Button>
            <Button variant="outline" type="button">
              Save & Exit
            </Button>
            <Button variant="primary" type="submit">
              Submit
            </Button>
          </div>
        </form>
      </div>

      <div className="demo-section">
        <h2>Input Variants</h2>
        <div className="demo-form">
          <Input
            label="Default Input"
            placeholder="Enter text"
            helperText="This is a helper text"
          />

          <Input
            label="Disabled Input"
            placeholder="This is disabled"
            value="Disabled value"
            disabled
          />

          <Input
            label="Error State"
            placeholder="Enter valid email"
            value="invalid-email"
            error
            errorMessage="Please enter a valid email address"
          />
        </div>
      </div>

      <div className="demo-section">
        <h2>Textarea Variants</h2>
        <div className="demo-form">
          <Textarea
            label="Default Textarea"
            placeholder="Enter your text here"
            helperText="You can resize this vertically"
          />

          <Textarea
            label="Non-resizable Textarea"
            placeholder="This cannot be resized"
            resize="none"
          />

          <Textarea
            label="With Drag Handle"
            placeholder="Textarea with drag handle"
            showDragHandle
          />
        </div>
      </div>

      <div className="demo-section">
        <h2>SelectDropdown Variants</h2>
        <div className="demo-form">
          <SelectDropdown
            label="Default Dropdown"
            placeholder="Select an option"
            options={[
              { value: '1', label: 'Option 1' },
              { value: '2', label: 'Option 2' },
              { value: '3', label: 'Option 3' },
            ]}
          />

          <SelectDropdown
            label="With Disabled Option"
            options={[
              { value: '1', label: 'Available Option' },
              { value: '2', label: 'Disabled Option', disabled: true },
              { value: '3', label: 'Another Available' },
            ]}
          />

          <SelectDropdown
            label="Error State"
            options={[
              { value: '1', label: 'Option 1' },
              { value: '2', label: 'Option 2' },
            ]}
            error
            errorMessage="Please select a valid option"
          />
        </div>
      </div>

      <div className="demo-section">
        <h2>RadioButtonGroup Variants</h2>
        <div className="demo-form">
          <RadioButtonGroup
            label="Default Radio Group"
            name="radio-demo-1"
            options={[
              { value: 'option1', label: 'Option 1' },
              { value: 'option2', label: 'Option 2' },
              { value: 'option3', label: 'Option 3' },
            ]}
          />

          <RadioButtonGroup
            label="With Info Icon"
            name="radio-demo-2"
            options={[
              { value: 'yes', label: 'Yes' },
              { value: 'no', label: 'No' },
            ]}
            showInfoIcon
            onInfoClick={() => alert('Info icon clicked!')}
          />

          <RadioButtonGroup
            label="With Disabled Option"
            name="radio-demo-3"
            options={[
              { value: 'enabled', label: 'Enabled Option' },
              { value: 'disabled', label: 'Disabled Option', disabled: true },
            ]}
          />
        </div>
      </div>

      <div className="demo-section">
        <h2>Multi-Step Form</h2>
        <p className="section-description">
          A comprehensive multi-step form wrapper with validation, navigation control, and state
          management. This demo shows a complete &quot;Add Banner&quot; workflow with 4 steps.
        </p>
        <div className="multistep-demo-wrapper">
          <MultiStepFormDemo steps={[{ label: "Neeraj", title: "Neeraj T", component: <h1>Hello 1</h1> }, { label: "Test", title: "Test T", component: <h1>Hello 2</h1> }]}/>
        </div>
      </div>
    </div>
  );
};

export default ComponentsDemo;
